import React, { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import {
  HoPreferencesOrganizationInfo,
  Button,
  SecurityPreferencesEmail,
  SecurityPreferencesPassword,
  PersonalInfoPreferences,
  useTwoFactorVerificationModal,
} from "../../../components";
import {
  GoogleAddressHelper,
  updatePasswordSchema,
  changeEmailSchema,
  personalInformationSchema,
  LodashHelper,
  DateHelper,
} from "../../../utils";
import {
  fetchHOPreferences,
  fetchHealthOrganizationPricing,
  fetchHealthOrganizationPricingHistory,
  postHealthOrganizationPricing,
  HOAdminUpdateOwnOrganization,
  updateHO
} from "../../../store/health_organizations";
import {
  changePassword,
  changePasswordVerification,
  changeEmail,
  updatePersonalInformation,
} from "../../../store/authorization";
import { useCurrentRole } from "../../../hooks/useCurrentRole";
import { updateOrganizationSchema } from "../../../utils/validations";

import { BillingAndInvoicing } from "../../../components/common/BillingAndInvoicing/BillingAndInvoicing";
import { ConfirmPricingTypeModal } from "../../../components/modals/ConfirmPricingActionModals/ConfirmPricingType";
import { ConfirmChangePricingValuesModal } from "../../../components/modals/ConfirmPricingActionModals/ConfirmChangePricingValuesModal";
import { BILLING_TABS_OBJECT } from "../../../constants/constants";
import { Divider, Timeline } from 'antd';
import styles from "./index.module.css";

const HO_PRICING_FIELD_LABELS = {
  flatRate: 'Ride Cost',
  additionalRate: 'Mileage Surcharge',
  holiday: 'Holiday',
  inclementWeather: 'Inclement Weather',
  overnightRide: 'Overnight Ride',
  rideNoShow: 'Rider No Show',
  sameDayCancellation: 'Same-Day Cancellation',
  waitTime: 'Wait Time',
  subscriptionFee: 'Subscription Fee',
  ridesPerMonth: 'Rides Per Month',
  pricingType: 'Pricing Type',
};

const HO_HIDDEN_PRICING_FIELDS = ['pricingStatus', 'updatedById', 'isTest'];

const HO_ADDITIONAL_RATE_LABELS = {
  milesSurcharge25_90: '25-90 Miles',
  milesSurcharge35_90: '35-90 Miles',
  milesSurcharge90Plus: '90+ Miles',
};

const HO_FLAT_RATE_TYPE_LABELS = {
  ambulatory: 'Ambulatory',
  stretcher: 'Stretcher',
  wheelchair: 'Wheelchair',
  isolation: 'Isolation',
  nonIsolation: 'Non-Isolation',
};

const HO_FLAT_RATE_MILE_LABELS = {
  miles0_3: '0-3 mi',
  moreThen3Miles: '>3 mi',
  miles0_10: '0-10 mi',
  miles10_25: '10-25 mi',
  miles0_25: '0-25 mi',
};

const computeInsertDiff = (oldVal, newVal) => {
  if (!oldVal || !newVal) return [];
  return Object.keys(HO_PRICING_FIELD_LABELS)
    .filter(field => JSON.stringify(oldVal[field]) !== JSON.stringify(newVal[field]))
    .map(field => ({ field, oldValue: oldVal[field], newValue: newVal[field] }));
};

const getHOVisibleChanges = (changes) =>
  (changes || []).filter(
    (c) =>
      !HO_HIDDEN_PRICING_FIELDS.includes(c.field) &&
      JSON.stringify(c.oldValue) !== JSON.stringify(c.newValue)
  );

const buildHOChangeRows = (oldVal, newVal) => {
  const rows = [];
  if (!newVal || typeof newVal !== 'object' || Array.isArray(newVal)) {
    if (newVal !== undefined && newVal !== null) {
      rows.push({
        label: 'Value',
        oldFee: oldVal !== undefined && String(oldVal) !== String(newVal) ? String(oldVal) : null,
        newFee: String(newVal),
      });
    }
    return rows;
  }

  // Simple { fee } or { fee, enabled } — add-on fees
  if (newVal.fee !== undefined) {
    const oldFee = oldVal?.fee;
    const newFee = newVal.fee;
    const wasEnabled = oldVal?.enabled !== false;
    const isEnabled = newVal.enabled !== false;
    if (!isEnabled && wasEnabled) {
      rows.push({ label: 'Fee', oldFee: `$${oldFee ?? newFee}`, newFee: 'Disabled' });
    } else if (isEnabled && !wasEnabled) {
      rows.push({ label: 'Fee', oldFee: '$0', newFee: `$${newFee}` });
    } else if (oldFee !== newFee) {
      rows.push({ label: 'Fee', oldFee: oldFee !== undefined ? `$${oldFee}` : null, newFee: `$${newFee}` });
    }
    return rows;
  }

  // Iterate over sub-keys to handle additionalRate and flatRate
  Object.keys(newVal).forEach((subKey) => {
    const oldSub = oldVal?.[subKey];
    const newSub = newVal[subKey];

    if (newSub && typeof newSub === 'object') {
      if (newSub.fee !== undefined) {
        // additionalRate sub-type: { milesSurcharge25_90: { fee: X } }
        const oldFee = oldSub?.fee;
        const newFee = newSub.fee;
        const wasEnabled = oldSub?.enabled !== false;
        const isEnabled = newSub.enabled !== false;
        const subLabel = HO_ADDITIONAL_RATE_LABELS[subKey] || HO_FLAT_RATE_TYPE_LABELS[subKey] || subKey;
        if (!isEnabled && wasEnabled) {
          rows.push({ label: subLabel, oldFee: `$${oldFee ?? newFee}`, newFee: 'Disabled' });
        } else if (isEnabled && !wasEnabled) {
          rows.push({ label: subLabel, oldFee: '$0', newFee: `$${newFee}` });
        } else if (oldFee !== newFee) {
          rows.push({ label: subLabel, oldFee: oldFee !== undefined ? `$${oldFee}` : null, newFee: `$${newFee}` });
        }
      } else {
        // flatRate sub-type: { ambulatory: { miles0_25: X } }
        const typeLabel = HO_FLAT_RATE_TYPE_LABELS[subKey] || subKey;
        Object.keys(newSub).forEach((mileKey) => {
          const oldPrice = oldSub?.[mileKey];
          const newPrice = newSub[mileKey];
          if (typeof newPrice === 'number' && oldPrice !== newPrice) {
            rows.push({
              label: `${typeLabel} (${HO_FLAT_RATE_MILE_LABELS[mileKey] || mileKey})`,
              oldFee: oldPrice !== undefined ? `$${oldPrice}` : null,
              newFee: `$${newPrice}`,
            });
          }
        });
      }
    } else if (newSub !== undefined && newSub !== null) {
      rows.push({
        label: subKey,
        oldFee: oldSub !== undefined && oldSub !== newSub ? String(oldSub) : null,
        newFee: String(newSub),
      });
    }
  });

  return rows;
};

const renderHOChangeCard = (change, idx) => {
  const label = HO_PRICING_FIELD_LABELS[change.field] || change.field;
  const rows = buildHOChangeRows(change.oldValue, change.newValue);

  if (!rows.length) return null;

  return (
    <div key={idx} className={styles.changeCard}>
      <div className={styles.changeCardTitle}>{label.toUpperCase()}</div>
      <div className={styles.changeCardDivider} />
      {rows.map((row, ri) => (
        <div key={ri} className={styles.changeCardRow}>
          <span className={styles.changeCardSubLabel}>{row.label}</span>
          <span className={styles.changeCardValues}>
            {row.oldFee && (
              <>
                <span className={styles.changeCardOldVal}>{row.oldFee}</span>
                <span className={styles.changeCardArrow}>{' → '}</span>
              </>
            )}
            <span className={styles.changeCardNewVal}>{row.newFee}</span>
          </span>
        </div>
      ))}
    </div>
  );
};

const getPersonalInfoInitialValues = (values) => {
  return {
    firstName: values?.firstName || "",
    lastName: values?.lastName || "",
    phoneNumber: values?.phoneNumber || "",
    jobTitle: values?.jobTitle || "",
    preferredTimezone: values?.preferredTimezone || null,
    emailSubscription: values?.emailSubscription === null ? false : values?.emailSubscription,
    userRole: values?.role
  };
};

const HO_Preferences = ({
  fetchHOPreferences,
  HOAdminUpdateOwnOrganization,
  postHealthOrganizationPricing,
  updateHO,
  fetchHealthOrganizationPricing,
  fetchHealthOrganizationPricingHistory,
  HOPreferences,
  changePassword,
  changeEmail,
  updatePersonalInformation,
  role,
  user,
  isViewDashboardMode,
  changePasswordVerification,
  HOPricing,
  HOPricingHistory,
  pricingType,
}) => {

  const [tab, setTab] = useState(BILLING_TABS_OBJECT[HOPricing?.pricingType] || BILLING_TABS_OBJECT.Transaction);

  const [currentValue, setCurrentValue] = useState();
  const [openModal, setOpenModal] = useState(false);
  const [openPricingValuesModal, setOpenPricingValuesModal] = useState(false);
  const securityRef = useRef(null);
  const timelineScrollRef = useRef(null);
  const [thumbTop, setThumbTop] = useState(0);
  const [thumbVisible, setThumbVisible] = useState(false);

  const THUMB_HEIGHT = 50;

  const handleTimelineScroll = () => {
    const el = timelineScrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight) { setThumbVisible(false); return; }
    setThumbVisible(true);
    const maxTop = clientHeight - THUMB_HEIGHT;
    setThumbTop(Math.round((scrollTop / (scrollHeight - clientHeight)) * maxTop));
  };
  const {
    TwoFactorAuthenticationModal,
    twoFactorModalVisible,
    setTwoFactorModalVisible,
  } = useTwoFactorVerificationModal({
    handleConfirm: (token, confirmCallback) =>
      changePasswordVerification({
        token,
        newPassword: securityRef?.current?.values?.newPassword,
      }).then((res) => {
        if (res) {
          confirmCallback();
          securityRef?.current?.resetForm();
        }
      }),
    email: user?.email,
  });
  const { isEmployee, isHOWorker } = useCurrentRole(role);

  useEffect(() => {
    fetchHOPreferences(); return () => { };
  }, [fetchHOPreferences]);

  useEffect(() => {
    if (isViewDashboardMode && user?.organizationId) {
      fetchHealthOrganizationPricingHistory({ orgId: user.organizationId });
    }
  }, [fetchHealthOrganizationPricingHistory, isViewDashboardMode, user?.organizationId]);

  useEffect(() => {
    const el = timelineScrollRef.current;
    if (!el) return;
    setThumbVisible(el.scrollHeight > el.clientHeight);
  }, [HOPricingHistory]);

  // useEffect(() => {
  //   (async function () {
  //     if (HOPreferences?.hoPricingId) {
  //       const payload = {
  //         orgId: user?.organizationId,
  //         pricingId: HOPreferences?.hoPricingId
  //       }
  //       await fetchHealthOrganizationPricing(payload);

  
  //      if(HOPreferences?.hoPricing[0]?.pricingType)
  //         setTab(BILLING_TABS_OBJECT[HOPreferences?.hoPricing[0]?.pricingType])
  //     }
  //   })();
  //   return;
  // }, [fetchHealthOrganizationPricing, HOPreferences, user?.organizationId]);

  // useEffect(() => {
  //   if (HOPricing) {
  //     if (HOPricing?.data) {
  //       setCurrentValue(HOPricing?.data[0]);
  //     } else {
  //       setCurrentValue(HOPricing);
  //     }

  //     if (HOPricing.pricingType) {
  //       setTab(BILLING_TABS_OBJECT[HOPricing?.pricingType])
  //     }
  //   }
  // }, [HOPricing]);

  useEffect(() => {

    if (HOPricing) {
      setCurrentValue(HOPricing);
      if (HOPricing.pricingType) {
        setTab(BILLING_TABS_OBJECT[HOPricing?.pricingType])
      }
    }

    return;
  }, [HOPricing]);

  const handleSelectAddress = async ({ value, values, setValues }) => {
    setValues({
      ...values,
      location: GoogleAddressHelper.transformFields(value),
    });
  };

  const handleChangeAddress = ({ value, values, setValues }) => {
    setValues({
      ...values,
      location: {
        formattedAddress: value,
      },
    });
  };

  const onChangeTab = async (key) => {
    if (key === BILLING_TABS_OBJECT.UberHealth) {
      setTab(BILLING_TABS_OBJECT.UberHealth);
      return;
    }

    const pricingType = BILLING_TABS_OBJECT[key] || BILLING_TABS_OBJECT.Transaction;

    const payload = {
      orgId: user?.organizationId,
      pricingType: pricingType,
    };

    await setTab(BILLING_TABS_OBJECT[key]);
    await fetchHealthOrganizationPricing(payload);
  };

  const onConfirm = (values) => {
    const { updatedBy, updatedAt, ...pricingValues } = values;
    postHealthOrganizationPricing({ ...pricingValues, id: HOPricing?.id, organizationId: user?.organizationId });
    setOpenModal(false);
    setOpenPricingValuesModal(false);
  }

  const onSubmitPricing = (values) => {

    const isPriceTypeChanged = pricingType !== tab;
    // HOPreferences?.hoPricing?.length > 0
    //   ? !(HOPreferences?.hoPricing[0]?.id === currentValue.id)
    //   : false;
    const isValuesChanged = !LodashHelper.isEqual(values, HOPricing);

    if (!HOPricing) {
      onConfirm(values)
    }
    else {
      setCurrentValue(values);
      if (isPriceTypeChanged) {
        setOpenModal(true);
      } else if (isValuesChanged && !isPriceTypeChanged) {
        setOpenPricingValuesModal(true);
      } else {
        setOpenModal(false);
        onConfirm(values)
      }
    }
  };

  return (
    <div className={styles.pageContent}>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>Preferences</h1>
      </div>
      <div className={styles.pageSection}>
        <h2> Organization Information</h2>
        <Formik
          onSubmit={(values) =>
            HOAdminUpdateOwnOrganization({
              ...values,
              airportBufferTime: DateHelper.convertTimeToMinutes(
                values?.airportBufferTime
              ),
              maxAdditionalPassengers: values?.maxAdditionalPassengers
                ? Number(values.maxAdditionalPassengers)
                : null,
            })
          }
          enableReinitialize
          validateOnMount
          validateOnChange={false}
          validationSchema={updateOrganizationSchema}
          initialValues={{
            name: HOPreferences?.name || "",
            location: { formattedAddress: "", ...HOPreferences?.location },
            airportBufferTime: DateHelper.convertMinutesToTime(
              HOPreferences?.airportBufferTime || 0
            ),
            maxAdditionalPassengers: HOPreferences?.maxAdditionalPassengers ?? '',
            rideRequestFields: HOPreferences?.rideRequestFields,
            parentOrgId: HOPreferences?.parentOrgId || null,
          }}
        >
          {({ handleSubmit, ...rest }) => {
            return (
              <form className={styles.form} onSubmit={handleSubmit}>
                <HoPreferencesOrganizationInfo
                  handleSelectAddress={handleSelectAddress}
                  handleChangeAddress={handleChangeAddress}
                  isViewDashboardMode={isViewDashboardMode}
                  rest={rest}
                  addressFieldIsDisabled={
                    (isEmployee && isHOWorker)
                  }
                />


                {role &&
                  !((isEmployee && isHOWorker)) && (
                    <div className={styles.btnPanel}>
                      <div className={styles.btnControl}>
                        <Button disabled={!rest.isValid} type="submit">
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  )}
              </form>
            );
          }}
        </Formik>
      </div>
      {isViewDashboardMode &&
        <div className={styles.pageSection}>
          <div className={styles.billingContainer}>
            <div className={styles.billingMain}>
              <BillingAndInvoicing
                user={user}
                tab={tab}
                isViewDashboardMode={isViewDashboardMode}
                onChange={onChangeTab}
                HOPricing={currentValue}
                onSubmitPricing={onSubmitPricing}
              />
            </div>
            {HOPricingHistory?.length > 0 && (
              <>
                <Divider type="vertical" className={styles.billingDivider} />
                <div className={styles.billingPanel}>
                  <div className={styles.panelTitleWrapper}>
                    <h2 className={styles.panelTitle}>Billing Update Logs</h2>
                  </div>
                  <div className={styles.scrollbarWrapper}>
                    <div
                      ref={timelineScrollRef}
                      className={styles.timelineScroll}
                      onScroll={handleTimelineScroll}
                    >
                      <Timeline
                        mode="left"
                        items={(() => {
                          const UNSET = {};
                          let prevInsertValue = UNSET;
                          return [...(HOPricingHistory || [])]
                            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                            .map((entry) => {
                              if (entry?.action === 'INSERT') {
                                const curr = prevInsertValue;
                                prevInsertValue = entry.value;
                                if (curr === UNSET) return entry;
                                const diff = computeInsertDiff(curr, entry.value);
                                return diff.length ? { ...entry, action: 'UPDATE', changes: diff } : null;
                              }
                              return entry;
                            })
                            .filter((entry) => entry && (entry.action === 'INSERT' || getHOVisibleChanges(entry?.changes).length > 0))
                            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                        })().map((entry) => {
                          const visibleChanges = getHOVisibleChanges(entry?.changes);
                          const date = new Date(entry?.createdAt);
                          const dateStr = date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago' });
                          const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'America/Chicago' }) + ' CST';
                          const name = entry?.updatedBy
                            ? `${entry.updatedBy.firstName || ''} ${entry.updatedBy.lastName || ''}`.trim() || '--'
                            : '--';
                          return {
                            dot: <span className={styles.timelineDot} />,
                            label: (
                              <div className={styles.dateLabel}>
                                <div>{dateStr}</div>
                                <div>{timeStr}</div>
                              </div>
                            ),
                            children: (
                              <div>
                                <div className={styles.entryHeader}>
                                  <span className={entry?.action === 'INSERT' ? styles.badgeCreated : styles.badgeUpdated}>
                                    {entry?.action === 'INSERT' ? 'Created' : 'Updated'}
                                  </span>
                                  <span className={styles.entryName}>{name}</span>
                                </div>
                                {visibleChanges.length > 0 && (
                                  <div className={styles.changeCardsWrapper}>
                                    {visibleChanges.map(renderHOChangeCard)}
                                  </div>
                                )}
                              </div>
                            ),
                          };
                        })}
                      />
                    </div>
                    {thumbVisible && (
                      <div className={styles.scrollTrack}>
                        <div
                          className={styles.scrollThumb}
                          style={{ top: thumbTop, height: THUMB_HEIGHT }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          {openModal &&
            ConfirmPricingTypeModal({
              handleConfirm: () => onConfirm(currentValue),
              handleCancel: () => setOpenModal(false)
            })}
          {openPricingValuesModal &&
            ConfirmChangePricingValuesModal({
              type: 'healthcare organization',
              handleConfirm: () => onConfirm(currentValue),
              handleCancel: () => setOpenPricingValuesModal(false)
            })}
        </div>
      }

      <div className={styles.pageSection}>
        <Formik
          enableReinitialize
          validateOnMount
          initialValues={getPersonalInfoInitialValues(
            isViewDashboardMode ? HOPreferences?.admin : user
          )}
          validationSchema={personalInformationSchema}
          onSubmit={({
            firstName,
            lastName,
            preferredTimezone,
            phoneNumber,
            jobTitle,
            emailSubscription
          }) =>
            updatePersonalInformation({
              firstName,
              lastName,
              preferredTimezone,
              phoneNumber,
              jobTitle,
              emailSubscription,
            })
          }
        >
          {({ values, handleSubmit, ...rest }) => {
            return (
              <form className={styles.form} onSubmit={handleSubmit}>
                <PersonalInfoPreferences
                  values={values}
                  disableAllFields={isViewDashboardMode}
                  allowScrollToPersonsalInfo={
                    !LodashHelper.isEmpty(HOPreferences) &&
                    !LodashHelper.isEmpty(user)
                  }
                />
                {!isViewDashboardMode && (
                  <div className={styles.btnPanel}>
                    <div className={styles.btnControl}>
                      <Button disabled={!rest.isValid || isViewDashboardMode} type="submit">
                        Save Changes
                      </Button>
                    </div>
                  </div>
                )}
              </form>
            );
          }}
        </Formik>
      </div>


      <div>
        <h2>Password Information</h2>
        <Formik
          enableReinitialize
          validateOnMount
          initialValues={
            HOPreferences && !(isEmployee && isHOWorker)
              ? {
                newEmail: HOPreferences?.admin?.email,
                password: "",
              }
              : { newEmail: user?.email || "", password: "" }
          }
          onSubmit={(values) => changeEmail(values)}
          validationSchema={changeEmailSchema(
            isEmployee && isHOWorker ? user?.email : HOPreferences?.admin?.email
          )}
        >
          {({ values, ...rest }) => {
            return (
              <form className={styles.form} onSubmit={rest.handleSubmit}>
                {HOPreferences?.admin && user && (
                  <SecurityPreferencesEmail
                    values={values}
                    initialEmail={
                      isEmployee && isHOWorker
                        ? user?.email
                        : HOPreferences?.admin?.email
                    }
                    disableAllFields={isViewDashboardMode}
                    {...rest}
                  />
                )}
              </form>
            );
          }}
        </Formik>
        <Formik
          innerRef={securityRef}
          validateOnMount
          initialValues={{
            newPassword: "",
            oldPassword: "",
          }}
          validationSchema={updatePasswordSchema}
          onSubmit={(values) => {
            changePassword(values).then((res) => setTwoFactorModalVisible(res));
          }}
        >
          {({ values, ...rest }) => {
            return (
              <form className={styles.form} onSubmit={rest.handleSubmit}>
                <SecurityPreferencesPassword
                  values={values}
                  disableAllFields={isViewDashboardMode}
                  {...rest}
                />
              </form>
            );
          }}
        </Formik>
      </div>
      {twoFactorModalVisible && TwoFactorAuthenticationModal}
    </div>
  );
};

const mapStateToProps = (state) => ({
  HOPreferences: state.healthOrganizations.HOPreferences,
  HOPricing: state.healthOrganizations.HOPricing,
  HOPricingHistory: state.healthOrganizations.HOPricingHistory,
  pricingType: state.healthOrganizations.pricingType,
  user: state.authorization.user,
  isViewDashboardMode: state?.authorization?.viewDashboardMode?.active,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateHO,
      fetchHOPreferences,
      HOAdminUpdateOwnOrganization,
      changePassword,
      changeEmail,
      updatePersonalInformation,
      changePasswordVerification,
      fetchHealthOrganizationPricing,
      fetchHealthOrganizationPricingHistory,
      postHealthOrganizationPricing,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(HO_Preferences);
